// Maps GET .../approval-artifacts → DvaArtifact for ArtifactRenderer

import type {
  ApprovalArtifactFinancialAssumption,
  ApprovalArtifactPayload,
  ApprovalArtifactRiskFactor,
} from '../services/approvalArtifactsService';
import type {
  ArtifactStatus,
  ArtifactType,
  DvaArtifact,
  DvaArtifactSection,
} from '../artifacts/types/artifactTypes';
import { formatAssumptionDisplayValue } from './formatFinancialDisplay';
import {
  expandScoringAbbreviations,
  formatExpectedOutcomeText,
  formatPlainExecutiveText,
  normalizePrimaryValueDriverText,
} from './executiveCopy';

const ARTIFACT_TYPE_IDS = new Set<string>([
  'executive_one_pager',
  'cfo_roi_summary',
  'board_rationale',
  'assumptions_appendix',
]);

function normalizeArtifactType(id: string): ArtifactType {
  if (ARTIFACT_TYPE_IDS.has(id)) return id as ArtifactType;
  return 'executive_one_pager';
}

function normalizeStatus(raw: string | undefined): ArtifactStatus {
  const s = (raw ?? 'draft').toLowerCase();
  if (s === 'final') return 'final';
  if (s === 'review' || s === 'in_review') return 'review';
  return 'draft';
}

function formatScenarioLabel(scenario: string): string {
  const key = scenario.toLowerCase();
  const labels: Record<string, string> = {
    conservative: 'Conservative',
    base: 'Base Case',
    aggressive: 'Aggressive',
  };
  return labels[key] ?? scenario;
}

/** Optional per-scenario ROI overrides from the execution kit (values already in $M). */
export type ScenarioRoiOverrides = Partial<
  Record<
    'conservative' | 'base' | 'aggressive',
    { roiMillions?: number; paybackMonths?: number | null }
  >
>;

function normalizeScenarioKey(scenario: string): 'conservative' | 'base' | 'aggressive' | null {
  const key = scenario.toLowerCase().replace(/\s+/g, '_');
  if (key === 'conservative') return 'conservative';
  if (key === 'base' || key === 'base_case') return 'base';
  if (key === 'aggressive') return 'aggressive';
  return null;
}

/** Format dollars or already-$M as a point estimate like `$13.1M`. */
function formatMillionsPoint(raw: number | null | undefined): string | null {
  if (raw == null || !Number.isFinite(raw) || raw <= 0) return null;
  const millions = Math.abs(raw) >= 1_000 ? raw / 1_000_000 : raw;
  return `$${millions.toFixed(1)}M`;
}

/** `~$13.1M/yr` → `$13.1M` */
function yieldToRoiPoint(yieldDisplay?: string | null): string | null {
  if (!yieldDisplay) return null;
  const match = yieldDisplay.match(/\$[\d.]+M/);
  return match ? match[0] : null;
}

function looksLikeRoiRange(display?: string | null): boolean {
  if (!display) return false;
  return /\$[\d.]+M\s*[–-]\s*\$[\d.]+M/.test(display);
}

/**
 * Per-scenario ROI should be that scenario's annual benefit (point), not the overall
 * cons–aggr band. Prefer annual_benefit → yield → kit override → API display.
 */
function resolveScenarioRoiDisplay(
  row: {
    scenario: string;
    roi_range_display?: string;
    annual_roi_display?: string;
    roi_low_display?: string;
    roi_high_display?: string;
    annual_benefit?: number;
    annual_yield_display?: string;
  },
  overrides?: ScenarioRoiOverrides,
  opts?: { forcePoint?: boolean },
): string {
  const fromBenefit = formatMillionsPoint(row.annual_benefit);
  if (fromBenefit) return fromBenefit;

  const fromYield = yieldToRoiPoint(row.annual_yield_display);
  if (fromYield) return fromYield;

  const key = normalizeScenarioKey(row.scenario);
  const fromKit = key ? formatMillionsPoint(overrides?.[key]?.roiMillions) : null;
  if (fromKit) return fromKit;

  const apiDisplay = row.roi_range_display ?? row.annual_roi_display ?? row.roi_low_display;
  if (opts?.forcePoint && looksLikeRoiRange(apiDisplay)) {
    // Duplicated overall band with no per-scenario benefit — still better to show low end
    // than pretend every scenario has the full band; kit override above should usually win.
    return apiDisplay?.split(/[–-]/)[0]?.trim() || apiDisplay || '—';
  }
  return apiDisplay ?? '—';
}

/** True when every scenario row shows the same ROI string (classic DVA-UI-040 bug). */
function scenarioRoiDisplaysAreDuplicated(displays: Array<string | null | undefined>): boolean {
  const normalized = displays.map(d => (d ?? '').trim()).filter(Boolean);
  if (normalized.length < 2) return false;
  return normalized.every(d => d === normalized[0]);
}

function resolveInitiativeSubtitle(payload: ApprovalArtifactPayload): string {
  return payload.header_card.initiative_name ?? payload.subtitle ?? payload.header_card.title;
}

/** Prefer measurable evidence when a narrative is only qualitative (DMD-1206). */
function withSupportingEvidence(
  narrative: string | null | undefined,
  evidence: string | null | undefined,
): string | undefined {
  const text = (narrative ?? '').trim();
  const support = (evidence ?? '').trim();
  if (!text && !support) return undefined;
  if (!text) return support;
  if (!support) return text;
  if (/\d/.test(text)) return text;
  return `${text} Supporting estimate: ${support}`;
}

function mapExpectedOutcomes(outcomes: string[] | null | undefined): string[] {
  return (outcomes ?? []).map(formatExpectedOutcomeText).filter(Boolean);
}

function mapAssumptionItemBlocks(
  assumptions: ApprovalArtifactFinancialAssumption[] | undefined,
  fallbackText: string | null | undefined,
  idPrefix: string,
): DvaArtifactSection['blocks'] {
  if (assumptions?.length) {
    return assumptions.map(a => {
      const noteParts = [
        a.source ? `Source: ${a.source}` : null,
        a.confidence_pct != null ? `Confidence: ${a.confidence_pct}%` : null,
      ].filter(Boolean);
      const categoryKey = (a.category ?? a.driver_type ?? '').toLowerCase();
      return {
        blockId: `${idPrefix}-${a.id}`,
        blockType: 'assumption_item' as const,
        label: a.category ?? a.driver_type,
        content: {
          label: a.label,
          value: formatAssumptionDisplayValue(
            a.label,
            a.value,
            a.value_display,
            a.unit
          ),
          note: noteParts.length ? noteParts.join(' · ') : undefined,
          category: categoryKey.includes('financial') ? 'financial' : categoryKey.includes('operational') ? 'operational' : 'data',
        },
      };
    });
  }
  return [{
    blockId: `${idPrefix}-empty`,
    blockType: 'paragraph',
    content: fallbackText ?? 'Details will be documented during Phase 1.',
  }];
}

function mapRiskFactorBlock(rf: ApprovalArtifactRiskFactor, blockId: string): DvaArtifactSection['blocks'][number] {
  const label = rf.title
    ? rf.title.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
    : 'Risk';
  return {
    blockId,
    blockType: 'risk_item',
    label: rf.title ?? label,
    content: { risk: rf.risk, mitigation: rf.mitigation },
  };
}

function buildArtifactMeta(payload: ApprovalArtifactPayload, confidencePct?: number): Pick<DvaArtifact, 'artifactId' | 'artifactType' | 'scope' | 'clientId' | 'initiativeId' | 'title' | 'subtitle' | 'summary' | 'metadata'> {
  const header = payload.header_card;
  return {
    artifactId: `artifact-${payload.artifact_id}-${payload.use_case_id}-v${payload.version}`,
    artifactType: normalizeArtifactType(payload.artifact_id),
    scope: 'initiative',
    clientId: payload.client_id,
    initiativeId: String(payload.use_case_id),
    // Titles are plain-text surfaces — strip leaked Markdown (DMD-1206).
    title: formatPlainExecutiveText(payload.title || header.title || 'Approval Artifact'),
    subtitle: formatPlainExecutiveText(resolveInitiativeSubtitle(payload)),
    // Summary keeps Markdown for ReactMarkdown; expand BV/EOI only.
    summary: expandScoringAbbreviations(header.summary_paragraph ?? ''),
    metadata: {
      version: payload.version,
      status: normalizeStatus(payload.document_status ?? header.status_badge),
      generatedAt: payload.generated_at ?? header.generated_date,
      generatedBy: 'system',
      confidenceScore: confidencePct ?? header.confidence_score_pct,
    },
  };
}

function mapExecutiveOnePager(
  payload: ApprovalArtifactPayload,
  scenarioOverrides?: ScenarioRoiOverrides,
): DvaArtifact {
  const header = payload.header_card;
  const sections: DvaArtifactSection[] = [];

  if (payload.initiative_overview) {
    const o = payload.initiative_overview;
    const evidence =
      normalizePrimaryValueDriverText(payload.strategic_objective?.primary_value_driver)
      || payload.financial_impact?.roi_range_base_display
      || null;
    const rationale = withSupportingEvidence(o.rationale, evidence);
    sections.push({
      sectionId: 'overview',
      title: 'Initiative Overview',
      subtitle: o.subtitle ?? 'What this initiative does and why it was selected',
      type: 'narrative',
      blocks: [
        {
          blockId: 'overview-tags',
          blockType: 'tag_row',
          content: [
            { label: 'Category', value: o.category ?? '—' },
            { label: 'Implementation Effort', value: o.implementation_effort ?? '—' },
            { label: 'Time to Value', value: o.time_to_value ?? '—' },
            { label: 'Confidence Score', value: o.confidence_display ?? '—' },
          ],
        },
        ...(rationale
          ? [{ blockId: 'overview-rationale', blockType: 'paragraph' as const, content: rationale }]
          : []),
      ],
    });
  }

  if (payload.strategic_objective) {
    const s = payload.strategic_objective;
    const outcomes = mapExpectedOutcomes(s.expected_outcomes);
    sections.push({
      sectionId: 'objective',
      title: 'Strategic Objective',
      subtitle: s.subtitle ?? 'Primary value driver and organizational goal',
      type: 'narrative',
      blocks: [
        ...(s.primary_value_driver
          ? [{
              blockId: 'objective-driver',
              blockType: 'callout' as const,
              label: 'Primary Value Driver',
              content: normalizePrimaryValueDriverText(s.primary_value_driver),
            }]
          : []),
        ...(outcomes.length
          ? [{ blockId: 'objective-outcomes', blockType: 'bullet_list' as const, label: 'Expected Outcomes', content: outcomes }]
          : []),
      ],
    });
  }

  if (payload.financial_impact) {
    const f = payload.financial_impact;
    const rawRows = f.scenario_analysis ?? [];
    const duplicated = scenarioRoiDisplaysAreDuplicated(rawRows.map(r => r.roi_range_display));
    const scenarioRows = rawRows.map(row => [
      formatScenarioLabel(row.scenario),
      duplicated || looksLikeRoiRange(row.roi_range_display)
        ? resolveScenarioRoiDisplay(row, scenarioOverrides, { forcePoint: true })
        : (row.roi_range_display ?? resolveScenarioRoiDisplay(row, scenarioOverrides)),
      row.payback_display,
    ]);

    sections.push({
      sectionId: 'financial-impact',
      title: 'Financial Impact',
      subtitle: f.subtitle ?? 'Estimated value and payback across scenarios',
      type: 'kpi_grid',
      blocks: [
        {
          blockId: 'financial-kpis',
          blockType: 'stat_group',
          content: [
            { label: 'ROI Range (Base)', value: f.roi_range_base_display ?? '—' },
            { label: 'Payback Period', value: f.payback_period_display ?? '—' },
            { label: 'Confidence Score', value: f.confidence_display ?? '—' },
          ],
        },
        ...(scenarioRows.length
          ? [{
              blockId: 'financial-scenarios',
              blockType: 'table' as const,
              label: 'Scenario Analysis',
              content: { headers: ['Scenario', 'ROI Range', 'Payback'], rows: scenarioRows },
            }]
          : []),
      ],
    });
  }

  if (payload.execution_plan) {
    const plan = payload.execution_plan;
    const phases = plan.phases ?? [];
    if (phases.length > 0) {
      sections.push({
        sectionId: 'execution-plan',
        title: 'Execution Plan',
        subtitle: plan.subtitle ?? plan.time_to_value ?? 'Phased delivery timeline',
        type: 'timeline',
        blocks: phases.map((phase, i) => ({
          blockId: `phase-${phase.phase ?? i + 1}`,
          blockType: 'timeline_phase' as const,
          label: `Phase ${phase.phase ?? i + 1}`,
          content: {
            title: phase.title || phase.label || `Phase ${i + 1}`,
            description: phase.description ?? '',
            duration: phase.duration ?? '',
          },
        })),
      });
    } else {
      sections.push({
        sectionId: 'execution-plan',
        title: 'Execution Plan',
        subtitle: plan.subtitle ?? 'Phased delivery timeline',
        type: 'narrative',
        blocks: [{
          blockId: 'execution-plan-empty',
          blockType: 'paragraph',
          content: plan.time_to_value ?? 'No implementation phases returned by the execution kit.',
        }],
      });
    }
  }

  if (payload.risks_mitigations) {
    const r = payload.risks_mitigations;
    const riskText = r.primary_risk_detail?.name ?? r.primary_risk ?? '—';
    const blocks: DvaArtifactSection['blocks'] = [{
      blockId: 'risk-primary',
      blockType: 'risk_item',
      label: 'Primary Risk',
      content: { risk: riskText, mitigation: r.mitigation ?? 'Mitigation plan pending.' },
    }];
    (r.dependency_risks ?? []).forEach((dep, i) => {
      blocks.push({
        blockId: `risk-dep-${i}`,
        blockType: 'risk_item',
        label: 'Dependency Risk',
        content: {
          risk: dep.name ?? dep.description ?? 'Dependency risk',
          mitigation: 'Tracked in Command Center with defined escalation path.',
        },
      });
    });
    sections.push({
      sectionId: 'risks',
      title: 'Risks & Mitigations',
      subtitle: r.subtitle ?? 'Key execution risks and planned response',
      type: 'risk_list',
      blocks,
    });
  }

  if (payload.recommendation?.text) {
    sections.push({
      sectionId: 'recommendation',
      title: 'Recommendation',
      subtitle: payload.recommendation.subtitle ?? 'Executive action required',
      type: 'narrative',
      blocks: [{ blockId: 'rec-callout', blockType: 'callout', label: 'Recommendation', content: payload.recommendation.text }],
    });
  }

  const confidencePct = header.confidence_score_pct ?? payload.initiative_overview?.confidence_score_pct;
  return { ...buildArtifactMeta(payload, confidencePct), sections };
}

function mapCfoRoiSummary(
  payload: ApprovalArtifactPayload,
  scenarioOverrides?: ScenarioRoiOverrides,
): DvaArtifact {
  const header = payload.header_card;
  const sections: DvaArtifactSection[] = [];

  if (payload.financial_impact_summary) {
    const f = payload.financial_impact_summary;
    sections.push({
      sectionId: 'financial-summary',
      title: 'Financial Impact Summary',
      subtitle: f.subtitle ?? 'Net present value and primary return drivers',
      type: 'kpi_grid',
      blocks: [
        {
          blockId: 'fin-kpis',
          blockType: 'stat_group',
          content: [
            { label: 'Annual ROI Range (USD)', value: f.roi_range_base_display ?? '—' },
            { label: 'Payback Period', value: f.payback_period_display ?? '—' },
            { label: 'Confidence Score', value: f.confidence_display ?? '—' },
            { label: 'Implementation Effort', value: f.implementation_effort ?? '—' },
          ],
        },
        ...(f.primary_value_driver
          ? [{
              blockId: 'fin-value-driver',
              blockType: 'callout' as const,
              label: 'Primary Value Driver',
              content: normalizePrimaryValueDriverText(f.primary_value_driver),
            }]
          : []),
      ],
    });
  }

  if (payload.projected_roi?.scenarios?.length) {
    const scenarios = payload.projected_roi.scenarios;
    const duplicated = scenarioRoiDisplaysAreDuplicated(
      scenarios.map(s => s.roi_range_display ?? `${s.roi_low_display ?? ''}|${s.roi_high_display ?? ''}`),
    );
    sections.push({
      sectionId: 'projected-roi',
      title: 'Projected ROI',
      subtitle: payload.projected_roi.subtitle ?? 'Three-scenario return on investment projection',
      type: 'kpi_grid',
      blocks: scenarios.map((s, i) => {
        // Prefer per-scenario annual benefit as a point estimate for both Low/High.
        // Fixes DVA-UI-040 where every card reused the overall cons–aggr band.
        const point =
          formatMillionsPoint(s.annual_benefit)
          ?? yieldToRoiPoint(s.annual_yield_display)
          ?? (duplicated || looksLikeRoiRange(s.roi_range_display) || looksLikeRoiRange(s.roi_low_display)
            ? resolveScenarioRoiDisplay(s, scenarioOverrides, { forcePoint: true })
            : null);
        const low = point ?? s.roi_low_display ?? '—';
        const high = point ?? s.roi_high_display ?? '—';
        return {
          blockId: `roi-${s.scenario}-${i}`,
          blockType: 'stat_group' as const,
          label: `${formatScenarioLabel(s.scenario)} Scenario`,
          content: [
            { label: 'ROI Low', value: low },
            { label: 'ROI High', value: high },
            { label: 'Payback', value: s.payback_display ?? '—' },
          ],
        };
      }),
    });
  }

  if (payload.payback_period?.rows?.length) {
    const rows = payload.payback_period.rows;
    const duplicated = scenarioRoiDisplaysAreDuplicated(rows.map(r => r.roi_range_display));
    sections.push({
      sectionId: 'payback',
      title: 'Payback Period',
      subtitle: payload.payback_period.subtitle ?? 'Investment recovery timeline by scenario',
      type: 'table',
      blocks: [{
        blockId: 'payback-table',
        blockType: 'table',
        content: {
          headers: ['Scenario', 'ROI Range', 'Payback Period', 'Annual Yield'],
          rows: rows.map(row => [
            formatScenarioLabel(row.scenario),
            duplicated || looksLikeRoiRange(row.roi_range_display) || Boolean(row.annual_yield_display)
              ? resolveScenarioRoiDisplay(row, scenarioOverrides)
              : (row.roi_range_display ?? '—'),
            row.payback_display,
            row.annual_yield_display ?? '—',
          ]),
        },
      }],
    });
  }

  if (payload.scenario_analysis) {
    const sa = payload.scenario_analysis;
    sections.push({
      sectionId: 'scenario-analysis',
      title: 'Scenario Analysis',
      subtitle: sa.subtitle ?? 'Key drivers and assumptions across scenarios',
      type: 'narrative',
      blocks: [
        ...(sa.narrative
          ? [{ blockId: 'scenario-narrative', blockType: 'paragraph' as const, content: sa.narrative }]
          : []),
        ...(sa.primary_scenario_risk
          ? [{ blockId: 'scenario-risk', blockType: 'callout' as const, label: 'Primary Scenario Risk', content: sa.primary_scenario_risk }]
          : []),
      ],
    });
  }

  if (payload.key_financial_assumptions) {
    const ka = payload.key_financial_assumptions;
    const assumptions = ka.assumptions ?? [];
    sections.push({
      sectionId: 'assumptions',
      title: 'Key Financial Assumptions',
      subtitle: ka.subtitle ?? 'Validated inputs underlying the ROI model',
      type: 'assumption_list',
      blocks: assumptions.length > 0
        ? assumptions.map(a => {
            const noteParts = [
              a.source ? `Source: ${a.source}` : null,
              a.confidence_pct != null ? `Confidence: ${a.confidence_pct}%` : null,
            ].filter(Boolean);
            return {
              blockId: `assumption-${a.id}`,
              blockType: 'assumption_item' as const,
              label: a.category,
              content: {
                label: a.label,
                value: formatAssumptionDisplayValue(
                  a.label,
                  a.value,
                  a.value_display,
                  a.unit
                ),
                note: noteParts.length ? noteParts.join(' · ') : undefined,
                category: a.category?.toLowerCase().includes('financial') ? 'financial' : 'operational',
              },
            };
          })
        : [{
            blockId: 'assumption-default',
            blockType: 'paragraph' as const,
            content: ka.fallback_text ?? 'Financial assumptions are documented in the full Assumptions Appendix for this initiative.',
          }],
    });
  }

  if (payload.cfo_recommendation?.text) {
    sections.push({
      sectionId: 'cfo-recommendation',
      title: 'CFO Recommendation',
      subtitle: payload.cfo_recommendation.subtitle ?? 'Finance leadership action required',
      type: 'narrative',
      blocks: [{
        blockId: 'cfo-rec',
        blockType: 'callout',
        label: 'Recommendation',
        content: payload.cfo_recommendation.text,
      }],
    });
  }

  return {
    ...buildArtifactMeta(payload, header.confidence_score_pct ?? payload.financial_impact_summary?.confidence_score_pct),
    sections,
  };
}

function mapBoardRationale(
  payload: ApprovalArtifactPayload,
  scenarioOverrides?: ScenarioRoiOverrides,
): DvaArtifact {
  const header = payload.header_card;
  const sections: DvaArtifactSection[] = [];

  if (payload.strategic_alignment) {
    const sa = payload.strategic_alignment;
    const tags = sa.tags;
    sections.push({
      sectionId: 'strategic-alignment',
      title: 'Strategic Alignment',
      subtitle: sa.subtitle ?? 'How this initiative supports organizational strategy',
      type: 'narrative',
      blocks: [
        ...(sa.narrative
          ? [{ blockId: 'alignment-narrative', blockType: 'paragraph' as const, content: sa.narrative }]
          : []),
        ...(tags
          ? [{
              blockId: 'alignment-tags',
              blockType: 'tag_row' as const,
              content: [
                { label: 'Category', value: tags.mission ?? '—' },
                { label: 'Implementation Effort', value: tags.organizational_priority ?? '—' },
                { label: 'Time to Value', value: tags.strategic_goal ?? '—' },
              ],
            }]
          : []),
      ],
    });
  }

  if (payload.problem_statement) {
    const ps = payload.problem_statement;
    sections.push({
      sectionId: 'problem-statement',
      title: 'Problem Statement',
      subtitle: ps.subtitle ?? 'The organizational gap this initiative addresses',
      type: 'narrative',
      blocks: [
        ...(ps.value_driver
          ? [{ blockId: 'problem-driver', blockType: 'callout' as const, label: 'Value Driver', content: ps.value_driver }]
          : []),
        ...(ps.cost_of_inaction
          ? [{ blockId: 'problem-risk', blockType: 'callout' as const, label: 'Cost of Inaction', content: ps.cost_of_inaction }]
          : []),
      ],
    });
  }

  if (payload.organizational_impact) {
    const oi = payload.organizational_impact;
    sections.push({
      sectionId: 'org-impact',
      title: 'Organizational Impact',
      subtitle: oi.subtitle ?? 'Operational and workforce implications',
      type: 'narrative',
      blocks: [
        ...(oi.narrative
          ? [{ blockId: 'impact-narrative', blockType: 'paragraph' as const, content: oi.narrative }]
          : []),
        ...(oi.expected_outcomes?.length
          ? [{
              blockId: 'impact-outcomes',
              blockType: 'bullet_list' as const,
              label: 'Expected Organizational Outcomes',
              content: mapExpectedOutcomes(oi.expected_outcomes),
            }]
          : []),
      ],
    });
  }

  if (payload.financial_benefits) {
    const fb = payload.financial_benefits;
    const rawRows = fb.scenario_summary ?? [];
    const duplicated = scenarioRoiDisplaysAreDuplicated(rawRows.map(r => r.annual_roi_display));
    const scenarioRows = rawRows.map(row => [
      formatScenarioLabel(row.scenario),
      duplicated || looksLikeRoiRange(row.annual_roi_display)
        ? resolveScenarioRoiDisplay(
            { scenario: row.scenario, annual_roi_display: row.annual_roi_display },
            scenarioOverrides,
            { forcePoint: true },
          )
        : (row.annual_roi_display ?? '—'),
      row.payback_display,
    ]);
    const fiveYearValue = fb.three_year_value_display ?? fb.cum_npv_5y_display;

    sections.push({
      sectionId: 'financial-benefits',
      title: 'Financial Benefits',
      subtitle: fb.subtitle ?? 'Board-level ROI summary',
      type: 'kpi_grid',
      blocks: [
        {
          blockId: 'fin-stats',
          blockType: 'stat_group',
          content: [
            { label: 'Annual ROI (Range)', value: fb.annual_revenue_display ?? '—' },
            { label: 'Payback Period', value: fb.payback_period_display ?? '—' },
            { label: '5-Year Value (Net)', value: fiveYearValue ?? '—' },
            { label: 'Confidence Range', value: fb.confidence_display ?? '—' },
          ],
        },
        ...(scenarioRows.length
          ? [{
              blockId: 'fin-scenarios',
              blockType: 'table' as const,
              label: 'Scenario Summary',
              content: { headers: ['Scenario', 'Annual ROI', 'Payback'], rows: scenarioRows },
            }]
          : []),
      ],
    });
  }

  if (payload.operational_benefits) {
    const ob = payload.operational_benefits;
    sections.push({
      sectionId: 'operational-benefits',
      title: 'Operational Benefits',
      subtitle: ob.subtitle ?? 'Quality, safety, and workflow improvements',
      type: 'bullet_list',
      blocks: (ob.bullets?.length ?? 0) > 0
        ? [{ blockId: 'ops-bullets', blockType: 'bullet_list', content: ob.bullets }]
        : [{
            blockId: 'ops-empty',
            blockType: 'paragraph',
            content: 'Operational benefit details will be documented as the initiative plan is finalized.',
          }],
    });
  }

  if (payload.execution_feasibility) {
    const ef = payload.execution_feasibility;
    const riskText = ef.primary_risk_detail?.name ?? ef.primary_adoption_risk ?? '—';
    sections.push({
      sectionId: 'execution-feasibility',
      title: 'Execution Feasibility',
      subtitle: ef.subtitle ?? 'Readiness assessment and delivery confidence',
      type: 'narrative',
      blocks: [
        ...(ef.narrative
          ? [{ blockId: 'feasibility-narrative', blockType: 'paragraph' as const, content: ef.narrative }]
          : []),
        {
          blockId: 'feasibility-risk',
          blockType: 'risk_item',
          label: 'Primary Execution Risk',
          content: {
            risk: riskText,
            mitigation: ef.mitigation ?? 'Phase-gated delivery with defined success criteria and executive sponsor accountability.',
          },
        },
      ],
    });
  }

  const boardRec = payload.recommendation?.board_rationale_summary ?? payload.recommendation?.text;
  if (boardRec) {
    sections.push({
      sectionId: 'board-recommendation',
      title: 'Recommendation',
      subtitle: payload.recommendation?.subtitle ?? 'Board action required',
      type: 'narrative',
      blocks: [{
        blockId: 'board-rec',
        blockType: 'callout',
        label: 'Board Recommendation',
        content: boardRec,
      }],
    });
  }

  return {
    ...buildArtifactMeta(payload, header.confidence_score_pct),
    sections,
  };
}

function mapAssumptionsAppendix(payload: ApprovalArtifactPayload): DvaArtifact {
  const header = payload.header_card;
  const sections: DvaArtifactSection[] = [];

  if (payload.financial_assumptions) {
    const fa = payload.financial_assumptions;
    sections.push({
      sectionId: 'financial-assumptions',
      title: 'Financial Assumptions',
      subtitle: fa.subtitle ?? 'Monetary inputs underlying the ROI model',
      type: 'assumption_list',
      blocks: mapAssumptionItemBlocks(fa.assumptions, fa.fallback_text, 'fin-assm'),
    });
  }

  if (payload.operational_assumptions) {
    const oa = payload.operational_assumptions;
    sections.push({
      sectionId: 'operational-assumptions',
      title: 'Operational Assumptions',
      subtitle: oa.subtitle ?? 'Workflow, adoption, and process inputs',
      type: 'assumption_list',
      blocks: mapAssumptionItemBlocks(oa.assumptions, oa.fallback_text, 'ops-assm'),
    });
  }

  if (payload.data_requirements) {
    const dr = payload.data_requirements;
    sections.push({
      sectionId: 'data-requirements',
      title: 'Data Requirements',
      subtitle: dr.subtitle ?? 'Data sources, access, and quality requirements',
      type: 'assumption_list',
      blocks: mapAssumptionItemBlocks(dr.assumptions, dr.fallback_text, 'data-assm'),
    });
  }

  if (payload.dependencies) {
    const dep = payload.dependencies;
    const items = dep.items ?? [];
    sections.push({
      sectionId: 'dependencies',
      title: 'Dependencies',
      subtitle: dep.subtitle ?? 'External inputs required for successful execution',
      type: items.length > 0 ? 'table' : 'narrative',
      blocks: items.length > 0
        ? [{
            blockId: 'deps-table',
            blockType: 'table',
            content: {
              headers: ['Dependency', 'Description', 'Severity'],
              rows: items.map(d => [
                d.label ?? '—',
                d.description ?? '—',
                d.severity ? d.severity.charAt(0).toUpperCase() + d.severity.slice(1) : 'Medium',
              ]),
            },
          }]
        : [{
            blockId: 'deps-empty',
            blockType: 'paragraph',
            content: dep.fallback_text ?? 'Dependencies will be documented during Phase 1 technical discovery.',
          }],
    });
  }

  if (payload.risk_factors) {
    const rf = payload.risk_factors;
    const riskBlocks: DvaArtifactSection['blocks'] = [];
    if (rf.primary) riskBlocks.push(mapRiskFactorBlock(rf.primary, 'risk-primary'));
    if (rf.adoption) riskBlocks.push(mapRiskFactorBlock(rf.adoption, 'risk-adoption'));
    if (rf.data_quality) riskBlocks.push(mapRiskFactorBlock(rf.data_quality, 'risk-data'));

    if (riskBlocks.length > 0) {
      sections.push({
        sectionId: 'risk-factors',
        title: 'Risk Factors',
        subtitle: rf.subtitle ?? 'Key uncertainties that affect model validity',
        type: 'risk_list',
        blocks: riskBlocks,
      });
    }
  }

  if (payload.scenario_inputs?.table) {
    const si = payload.scenario_inputs;
    const params = si.table?.parameters ?? [];
    const rows = params.map(p => [p.parameter, p.conservative, p.base, p.aggressive]);

    sections.push({
      sectionId: 'scenario-inputs',
      title: 'Scenario Inputs',
      subtitle: si.subtitle ?? 'Full parameter table underlying the three-scenario model',
      type: 'table',
      blocks: [
        {
          blockId: 'scenario-table',
          blockType: 'table',
          content: {
            headers: ['Parameter', 'Conservative', 'Base Case', 'Aggressive'],
            rows,
          },
        },
        ...(si.table?.note
          ? [{ blockId: 'scenario-note', blockType: 'paragraph' as const, content: si.table.note }]
          : []),
      ],
    });
  }

  return {
    ...buildArtifactMeta(payload, header.confidence_score_pct),
    sections,
  };
}

export function mapApprovalArtifactToDva(
  payload: ApprovalArtifactPayload,
  scenarioOverrides?: ScenarioRoiOverrides,
): DvaArtifact {
  switch (payload.artifact_id) {
    case 'cfo_roi_summary':
      return mapCfoRoiSummary(payload, scenarioOverrides);
    case 'board_rationale':
      return mapBoardRationale(payload, scenarioOverrides);
    case 'assumptions_appendix':
      return mapAssumptionsAppendix(payload);
    default:
      return mapExecutiveOnePager(payload, scenarioOverrides);
  }
}
